import { useCallback, useRef } from 'react';

import {
  useSplitDetailPanelContext,
  useSplitDetailPanelSelector,
} from './useSplitDetailPanelStore';

import type { PanelComponent, ResourceItem, TabItem, TabKey } from '../types';

export type ReconcileStrategy = 'lru' | 'fifo' | 'user-choice';

export interface UseReconcileTabsOptions {
  /**
   * Callback when tab limit is reached and user-choice strategy is selected.
   * Should return the TabKey to remove, or null to cancel the operation.
   */
  onTabLimitReached?: (currentTabs: TabItem[], newTab: TabItem) => Promise<TabKey | null>;
  /**
   * Strategy for determining which tab to remove when limit is reached
   * - 'lru': Least Recently Used (default)
   * - 'fifo': First In First Out
   * - 'user-choice': Prompt user to choose which tab to close
   */
  strategy?: ReconcileStrategy;
}

export interface UseReconcileTabsReturn {
  /** Add a new tab with automatic reconciliation if limit is reached */
  addTabWithReconcile: (
    resourceType: string,
    item: ResourceItem,
    component: PanelComponent,
    activate?: boolean,
    label?: string,
  ) => Promise<boolean>;
  /** Whether a new tab can be added without reconciliation (reactive) */
  canAddTab: boolean;
  /** Get the tab key that would be removed based on current strategy */
  getTabToRemove: () => TabKey | null;
  /** Switch to a tab and track access time for LRU strategy */
  switchTabWithTrack: (tabKey: TabKey) => void;
}

interface TabAccessRecord {
  key: TabKey;
  lastAccessed: number;
}

/**
 * Hook for managing tab reconciliation when tab limit is reached.
 * Tracks tab access patterns and provides strategies for removing tabs.
 *
 * @example
 * ```tsx
 * // LRU strategy (default)
 * const { addTabWithReconcile, canAddTab, switchTabWithTrack } = useReconcileTabs();
 *
 * const handleOpenDetail = async (item: Subnet) => {
 *   if (!canAddTab) {
 *     toast.warn('Maximum tabs limit reached');
 *   }
 *   const success = await addTabWithReconcile('subnet', item, SubnetPanel, true, item.name);
 *   if (success) {
 *     console.log('Tab opened successfully');
 *   }
 * };
 *
 * const handleTabClick = (tabKey: TabKey) => {
 *   switchTabWithTrack(tabKey); // Automatically tracks LRU access time
 * };
 *
 * // User choice strategy
 * const { addTabWithReconcile } = useReconcileTabs({
 *   strategy: 'user-choice',
 *   onTabLimitReached: async (currentTabs, newTab) => {
 *     const selectedKey = await showTabSelectionDialog(currentTabs);
 *     return selectedKey;
 *   },
 * });
 * ```
 */
export function useReconcileTabs(options: UseReconcileTabsOptions = {}): UseReconcileTabsReturn {
  // Phase 7-Tab: 기본 전략을 FIFO로 변경 (요구사항 반영)
  const { strategy = 'fifo', onTabLimitReached } = options;

  const { store, maxTabs } = useSplitDetailPanelContext();

  // Track tab access timestamps for LRU strategy
  const accessRecordsRef = useRef<Map<TabKey, number>>(new Map());

  // Update access timestamp when a tab is accessed
  const updateAccessTime = useCallback((key: TabKey) => {
    accessRecordsRef.current.set(key, Date.now());
  }, []);

  // Initialize access records for existing tabs
  const initializeAccessRecords = useCallback(() => {
    const { tabs, activeTabKey } = store.getState();
    const currentTime = Date.now();

    tabs.forEach((tab, index) => {
      if (!accessRecordsRef.current.has(tab.key)) {
        // Initialize with staggered timestamps based on tab order (older first)
        accessRecordsRef.current.set(tab.key, currentTime - (tabs.length - index) * 1000);
      }
    });

    // Mark active tab as most recently accessed
    if (activeTabKey) {
      accessRecordsRef.current.set(activeTabKey, currentTime);
    }
  }, [store]);

  // Get the tab key to remove based on strategy
  const getTabToRemove = useCallback((): TabKey | null => {
    const { tabs } = store.getState();

    if (tabs.length === 0) {
      return null;
    }

    initializeAccessRecords();

    switch (strategy) {
      case 'fifo': {
        // First In First Out - remove the oldest tab (first in array)
        return tabs[0].key;
      }

      case 'lru': {
        // Least Recently Used - remove the tab with oldest access time
        let oldestKey: TabKey | null = null;
        let oldestTime = Infinity;

        for (const tab of tabs) {
          const accessTime = accessRecordsRef.current.get(tab.key) || 0;
          if (accessTime < oldestTime) {
            oldestTime = accessTime;
            oldestKey = tab.key;
          }
        }

        return oldestKey;
      }

      case 'user-choice': {
        // For user-choice strategy, return null as decision is async
        return null;
      }

      default: {
        return null;
      }
    }
  }, [store, strategy, initializeAccessRecords]);

  // Check if a new tab can be added without reconciliation (reactive)
  const canAddTab = useSplitDetailPanelSelector(state => state.tabs.length < maxTabs);

  // Add a new tab with reconciliation if needed
  const addTabWithReconcile = useCallback(
    async (
      resourceType: string,
      item: ResourceItem,
      component: PanelComponent,
      activate = true,
      label?: string,
    ): Promise<boolean> => {
      const { tabs, activeTabKey } = store.getState();
      const newTabKey = `${resourceType}:${item.id}` as TabKey;

      // Check if tab already exists
      const existingTab = tabs.find(tab => tab.key === newTabKey);
      if (existingTab) {
        // Tab exists, just update and activate
        store.getState().openTab(resourceType, item, component, activate, label);

        // Update access time
        updateAccessTime(newTabKey);

        return true;
      }

      // Check if we need to reconcile
      if (tabs.length < maxTabs) {
        // Can add without reconciliation
        store.getState().openTab(resourceType, item, component, activate, label);

        // Track new tab access time
        updateAccessTime(newTabKey);

        return true;
      }

      // Tab limit reached - need to reconcile
      initializeAccessRecords();

      // Update active tab access time before reconciliation
      if (activeTabKey) {
        updateAccessTime(activeTabKey);
      }

      const newTab: TabItem = {
        key: newTabKey,
        resourceType,
        resourceId: item.id,
        item,
        component,
        label,
      };

      let tabKeyToRemove: TabKey | null = null;

      if (strategy === 'user-choice') {
        if (!onTabLimitReached) {
          console.warn(
            'useReconcileTabs: user-choice strategy requires onTabLimitReached callback',
          );
          return false;
        }

        // Ask user which tab to close
        tabKeyToRemove = await onTabLimitReached(tabs, newTab);

        if (!tabKeyToRemove) {
          // User cancelled the operation
          return false;
        }

        // Validate that the selected key exists
        if (!tabs.some(tab => tab.key === tabKeyToRemove)) {
          console.error('useReconcileTabs: invalid tab key selected for removal');
          return false;
        }
      } else {
        // Automatic strategy (LRU or FIFO)
        tabKeyToRemove = getTabToRemove();

        if (!tabKeyToRemove) {
          console.error('useReconcileTabs: could not determine tab to remove');
          return false;
        }
      }

      // Remove the old tab
      store.getState().closeTab(tabKeyToRemove);

      // Clean up access record for removed tab
      accessRecordsRef.current.delete(tabKeyToRemove);

      // Open the new tab
      store.getState().openTab(resourceType, item, component, activate, label);

      // Track new tab access time
      updateAccessTime(newTabKey);

      return true;
    },
    [
      store,
      maxTabs,
      strategy,
      onTabLimitReached,
      getTabToRemove,
      updateAccessTime,
      initializeAccessRecords,
    ],
  );

  // Switch tab and update LRU tracking
  const switchTabWithTrack = useCallback(
    (tabKey: TabKey) => {
      updateAccessTime(tabKey);
      store.getState().switchTab(tabKey);
    },
    [store, updateAccessTime],
  );

  return {
    canAddTab,
    addTabWithReconcile,
    getTabToRemove,
    switchTabWithTrack,
  };
}

/**
 * Helper hook to track tab switches for LRU strategy.
 * Use this in conjunction with switchTab action to maintain accurate access records.
 *
 * @example
 * ```tsx
 * const { trackAccess } = useTabAccessTracker();
 * // Phase 6: useSplitDetailPanelActions → useTabPanelActionStoreInternal
 * const { switchTab } = useTabPanelActionStoreInternal();
 *
 * const handleTabClick = (key: TabKey) => {
 *   trackAccess(key);
 *   switchTab(key);
 * };
 * ```
 */
export function useTabAccessTracker() {
  const accessRecordsRef = useRef<Map<TabKey, number>>(new Map());

  const trackAccess = useCallback((key: TabKey) => {
    accessRecordsRef.current.set(key, Date.now());
  }, []);

  const getAccessTime = useCallback((key: TabKey): number => {
    return accessRecordsRef.current.get(key) || 0;
  }, []);

  const clearAccessRecord = useCallback((key: TabKey) => {
    accessRecordsRef.current.delete(key);
  }, []);

  const resetAccessRecords = useCallback(() => {
    accessRecordsRef.current.clear();
  }, []);

  return {
    trackAccess,
    getAccessTime,
    clearAccessRecord,
    resetAccessRecords,
  };
}
